import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetch3, fetch4, fetch5 } from "../../../../commonApis/commonApis";

const url = process.env.REACT_APP_BASE_URL || "";

export const locationKeys = {
  all: ["location"] as const,
  ALL_COUNTRIES: ["allCountries"] as const,
  lists: () => [...locationKeys.all, "list"] as const,
};

export const cityKeys = {
  all: ["cities"] as const,
};

export const districtKeys = {
  all: ["districts"] as const,
};

export const useAllCountries = () => {
  return useQuery({
    queryKey: locationKeys.ALL_COUNTRIES,
    queryFn: async () => {
      const res = await fetch3(`${url}/api/countries/all`, "get");
      return res?.data || [];
    },
  });
};

export const useAllCities = (countryName: string) => {
  return useQuery({
    queryKey: ["allCities", countryName],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/countries/${countryName}/cities?type=LM`, "get");
      return res?.data || [];
    },
    enabled: !!countryName,
  });
};

export const useLocationList = (params: { page?: number; searchFor?: string; limit?: number }) => {
  const { page = 1, searchFor = "", limit = 10 } = params;
  return useQuery({
    queryKey: [...locationKeys.lists(), page, searchFor, limit],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/countries?page=${page}&searchFor=${searchFor}&limit=${limit}&type=LM`, "get");
      return res;
    },
  });
};

export const useCreateLocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res = await fetch4(`${url}/api/countries`, { ...payload, type: "LM" });
      if (!res?.success) {
        throw new Error(res?.message || "Failed to create country");
      }
      return res?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.all });
    },
  });
};

export const useUpdateLocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: any }) => {
      const res = await fetch5(`${url}/api/countries/${id}`, "PATCH", { ...payload, type: "LM" });
      if (!res?.success) {
        throw new Error(res?.message || "Failed to update country");
      }
      return res?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.all });
    },
  });
};

export const useDeleteLocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch5(`${url}/api/countries/${id}`, "DELETE", { type: "LM" });
      if (!res?.success) {
        throw new Error(res?.message || "Failed to delete country");
      }
      return res?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.all });
    },
  });
};

// --- City Hooks ---
export const useCityList = (params: { countryId: string; page: number; searchFor: string; limit?: number }) => {
  const { countryId, page = 1, searchFor = "", limit = 10 } = params;
  return useQuery({
    queryKey: [...cityKeys.all, "list", countryId, page, searchFor, limit],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/cities?countryId=${countryId}&page=${page}&searchFor=${searchFor}&limit=${limit}&type=LM`,
        "get"
      );
      return res;
    },
    enabled: !!countryId,
  });
};

export const useCreateCity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res = await fetch4(`${url}/api/cities`, { ...payload, type: "LM" });
      if (!res?.success) {
        throw new Error(res?.message || "Failed to create city");
      }
      return res?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cityKeys.all });
    },
  });
};

export const useUpdateCity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: any }) => {
      const res = await fetch5(`${url}/api/cities/${id}`, "PATCH", { ...payload, type: "LM" });
      if (!res?.success) {
        throw new Error(res?.message || "Failed to update city");
      }
      return res?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cityKeys.all });
    },
  });
};

// --- District Hooks ---
export const useDistrictList = (params: {
  countryId: string;
  cityId: string;
  page: number;
  searchFor: string;
  limit?: number;
}) => {
  const { countryId, cityId, page = 1, searchFor = "", limit = 10 } = params;
  return useQuery({
    queryKey: [...districtKeys.all, "list", countryId, cityId, page, searchFor, limit],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/districts?countryId=${countryId}&cityId=${cityId}&page=${page}&searchFor=${searchFor}&limit=${limit}&type=LM`,
        "get"
      );
      return res;
    },
    enabled: !!cityId,
  });
};

export const useCreateDistrict = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res = await fetch4(`${url}/api/districts`, { ...payload, type: "LM" });
      if (!res?.success) {
        throw new Error(res?.message || "Failed to create district");
      }
      return res?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: districtKeys.all });
    },
  });
};

export const useUpdateDistrict = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: any }) => {
      const res = await fetch5(`${url}/api/districts/${id}`, "PATCH", { ...payload, type: "LM" });
      if (!res?.success) {
        throw new Error(res?.message || "Failed to update district");
      }
      return res?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: districtKeys.all });
    },
  });
};
